import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

/*
 * The bar that says "this is not your account".
 *
 * Support access has no restrictions on what it can do, so the one thing this must never do is
 * be missable. Three decisions follow from that:
 *
 *  - It is rendered above the router, not inside a layout, so it is on EVERY screen including
 *    the ones an admin does not normally see.
 *  - It reads `user.impersonated_by`, which /auth/me reports from the token itself. React state
 *    would forget on refresh and leave an admin acting as somebody else with nothing on screen
 *    saying so.
 *  - The colour is deliberately not the brand orange. It has to read as a warning rather than
 *    as product chrome, at a glance, in a screenshot, six weeks later.
 *
 * The spacer keeps the fixed bar from covering the top of whatever page is underneath.
 */
const SupportSessionBanner = () => {
  const { user, impersonating, endSupportSession } = useAuth();
  const navigate = useNavigate();
  const [leaving, setLeaving] = useState(false);

  if (!impersonating) return null;

  const leave = () => {
    setLeaving(true);
    endSupportSession();
    navigate('/admin/partners', { replace: true });
  };

  return (
    <>
      <div className="h-11" aria-hidden="true" />
      <div
        role="status"
        data-testid="support-session-banner"
        className="fixed inset-x-0 top-0 z-50 flex h-11 items-center justify-center gap-3
                   bg-[#B45309] px-4 text-sm text-white shadow-md"
      >
        <ShieldAlert className="h-4 w-4 shrink-0" />
        <span className="truncate">
          You are signed in as <span className="font-semibold">{user?.full_name}</span>
          {user?.impersonator_name ? <> — support session opened by {user.impersonator_name}</> : null}
          . Everything you do here is recorded against you.
        </span>
        <button
          type="button"
          onClick={leave}
          disabled={leaving}
          data-testid="end-support-session"
          className="shrink-0 rounded-md bg-white/15 px-3 py-1 text-xs font-semibold
                     transition-colors hover:bg-white/25 disabled:opacity-60"
        >
          {leaving ? 'Leaving…' : 'Back to admin'}
        </button>
      </div>
    </>
  );
};

export default SupportSessionBanner;
